import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import ShareButton from "@/components/ShareButton";
import { API } from "@/lib/api";

/**
 * Prominent post-recommendations CTA inviting the user to share their
 * personalised reading list. Reuses the existing <ShareButton> popover so
 * channel logic (WhatsApp / IG / native / copy) stays in one place.
 *
 * Renders a horizontal strip of book covers as a visual preview of what's
 * being shared — turns the abstract URL into something tangible and shareable.
 */
export default function ShareReadingListCTA({ result }) {
    const { t } = useTranslation();

    if (!result?.recommendations?.length) return null;

    const count = result.recommendations.length;
    const titlesShort = result.recommendations
        .slice(0, 3)
        .map((b) => b.title)
        .join(" · ");

    const shareText = t("share.list_default_text", {
        basedOn: result.based_on,
        titles: titlesShort,
    });

    return (
        <section
            data-testid="share-reading-list-cta"
            className="relative mt-16 mb-4 border border-[var(--border-strong)] bg-[var(--bg-elevated)]"
        >
            <div className="grid md:grid-cols-[1fr_auto] gap-8 md:gap-12 p-8 md:p-10 items-center">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={14} strokeWidth={1.5} className="text-[var(--accent)]" />
                        <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--accent)]">
                            {t("share.list_eyebrow")}
                        </p>
                    </div>
                    <h3
                        className="font-display text-3xl md:text-4xl text-[var(--ink)] italic mb-3"
                        style={{ fontVariationSettings: '"wght" 500, "wdth" 90, "opsz" 96, "slnt" -10' }}
                    >
                        {t("share.list_title", { count })}
                    </h3>
                    <p className="text-sm md:text-base text-[var(--ink-muted)] leading-relaxed max-w-xl">
                        {t("share.list_lead", { basedOn: result.based_on })}
                    </p>

                    {/* Mini cover strip — visual proof of what's inside the link */}
                    <div className="mt-5 flex items-center gap-2">
                        {result.recommendations.slice(0, 6).map((book, i) => (
                            <CoverThumb key={book.id || i} book={book} index={i} />
                        ))}
                    </div>
                </div>

                <div className="flex md:flex-col items-center md:items-end gap-3 md:min-w-[200px]">
                    <ShareButton
                        recId={result.id}
                        basedOn={result.based_on}
                        textOverride={shareText}
                        label={t("share.list_share_btn")}
                        testIdPrefix="share-list"
                        ogImageUrl={`${API}/og/r/${result.id}.jpg`}
                    />
                    <span className="text-[10px] uppercase tracking-widest text-[var(--ink-dim)]">
                        {t("share.list_count_books", { count })}
                    </span>
                </div>
            </div>
        </section>
    );
}

function CoverThumb({ book, index }) {
    const url = book.cover_url;
    if (!url) {
        return (
            <div
                data-testid={`share-list-thumb-${index}`}
                className="w-10 h-14 md:w-12 md:h-16 bg-[var(--bg-strong)] border border-[var(--border)] flex items-center justify-center"
                aria-hidden="true"
            >
                <span className="text-[8px] uppercase tracking-widest text-[var(--ink-dim)]">
                    {(book.title || "?").slice(0, 2)}
                </span>
            </div>
        );
    }
    return (
        <img
            data-testid={`share-list-thumb-${index}`}
            src={url}
            alt={book.title}
            loading="lazy"
            className="w-10 h-14 md:w-12 md:h-16 object-cover border border-[var(--border-strong)]"
            onError={(e) => {
                e.currentTarget.style.display = "none";
            }}
        />
    );
}
